import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Grid2,
    Stack,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import {
    AGE_BAND_TYPES,
    BOOKING_STATUS_COLORS,
    BOOKING_STATUSES,
    INTEGRATION_DISPLAY_NAMES,
    TABLE_ROW_COUNT_OPTIONS,
} from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useTranslation } from 'react-i18next';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { cancelBooking, simpleRefundBooking } from '@openapi/bookings/bookings';
import useNotify from '../../../hooks/useNotify';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { useCapabilities } from '../../../hooks/useCapabilities';
import BookingsMassActionDialog from './mass-action/BookingsMassActionDialog';
import BookingRowActions from './mass-action/BookingRowActions';
import useBookingsMassAction from './mass-action/useBookingsMassAction';
import { BOOKINGS_TABLE_SORTABLE_COLUMNS } from './mass-action/bookingsTableColumns';

const ContactLine = ({ label, value }) =>
    value ? (
        <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                {label}
            </Typography>
            <Typography variant="caption">{value}</Typography>
        </Box>
    ) : null;

const TicketTypesCell = ({ ticketTypes, t }) => {
    if (!ticketTypes?.length) return '-';
    const ageBandLabels = {
        [AGE_BAND_TYPES.ADULT]: t('ticketoptions.ageBand.adult'),
        [AGE_BAND_TYPES.CHILD]: t('ticketoptions.ageBand.child'),
        [AGE_BAND_TYPES.YOUTH]: t('ticketoptions.ageBand.youth'),
        [AGE_BAND_TYPES.INFANT]: t('ticketoptions.ageBand.infant'),
        [AGE_BAND_TYPES.SENIOR]: t('ticketoptions.ageBand.senior'),
    };
    const summary = ticketTypes.map((tt) => `${tt.count} × ${tt.name}`).join(', ');
    return (
        <Tooltip
            title={
                <Box sx={{ p: 0.5 }}>
                    {ticketTypes.map((tt, i) => (
                        <Box key={i} sx={{ mb: i < ticketTypes.length - 1 ? 1 : 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {`${tt.count} × ${tt.name}`}
                            </Typography>
                            {tt.options?.length > 0 &&
                                tt.options.map((opt, j) => (
                                    <Typography
                                        key={j}
                                        variant="body2"
                                        sx={{ pl: 1.5, color: 'rgba(255,255,255,0.75)' }}
                                    >
                                        {`${opt.count} × ${opt.name}${opt.ageBand ? ` (${ageBandLabels[opt.ageBand] ?? opt.ageBand})` : ''}`}
                                    </Typography>
                                ))}
                        </Box>
                    ))}
                </Box>
            }
            arrow
            placement="bottom"
        >
            <span
                style={{
                    cursor: 'pointer',
                    textDecoration: 'underline dotted',
                    textUnderlineOffset: 3,
                }}
            >
                {summary}
            </span>
        </Tooltip>
    );
};

// formats milliseconds as m:ss countdown
const formatRollbackRemaining = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

// Hourglass badge on the status chip for bookings with a pending order.
// Hover reveals a live countdown until the unpaid items are rolled back.
// The countdown only ticks while the tooltip is open, so idle rows don't
// run intervals or re-render.
const RollbackIndicator = ({ rollbackTimeAt, t }) => {
    const [open, setOpen] = useState(false);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!open) return undefined;
        setNow(Date.now());
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [open]);

    const remainingMs = new Date(rollbackTimeAt).getTime() - now;
    const isOverdue = remainingMs <= 0;
    const color = isOverdue ? '#d9534f' : '#f0ad4e';

    return (
        <Tooltip
            title={
                isOverdue
                    ? t('bookings.rollback.overdue')
                    : t('bookings.rollback.timeRemaining', {
                          time: formatRollbackRemaining(remainingMs),
                      })
            }
            arrow
            placement="bottom"
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
        >
            <HourglassBottomIcon
                sx={{
                    position: 'absolute',
                    top: -5,
                    left: -5,
                    fontSize: 15,
                    color,
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    border: `1.5px solid ${color}`,
                    padding: '1px',
                    cursor: 'pointer',
                }}
            />
        </Tooltip>
    );
};

const GuestCell = ({ row, t }) => (
    <Tooltip
        title={
            <Box sx={{ p: 0.5 }}>
                <ContactLine label={t('bookings.columns.email')} value={row.email} />
                <ContactLine label={t('bookings.columns.phone')} value={row.phone} />
            </Box>
        }
        arrow
        placement="bottom"
    >
        <span
            style={{
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 3,
            }}
        >
            {`${row.firstName} ${row.lastName}`}
        </span>
    </Tooltip>
);

const BookingsTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    sort,
    sortDir,
    setSorting,
    filters,
    refreshBookings,
}) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const { showModal } = useModals();
    const { can, CAPABILITIES } = useCapabilities();
    const navigate = useNavigate();
    const [operatingId, setOperatingId] = useState(null);

    const canUseMassAction = can([CAPABILITIES.BOOKINGS_CANCEL, CAPABILITIES.BOOKINGS_REFUND]);
    const canRefundBookings = can(CAPABILITIES.BOOKINGS_REFUND);
    const canAlwaysRefund = can(CAPABILITIES.BOOKINGS_ALWAYS_REFUND);
    const canCancelBookings = can(CAPABILITIES.BOOKINGS_CANCEL);

    const isRowMassOperable = useCallback(
        (row) =>
            row.status === BOOKING_STATUSES.FINISHED &&
            !row?.limitedModification &&
            row?.timeslot?.deleted !== true,
        [],
    );

    const {
        selectedCount,
        massSelectedCount,
        selectedIds,
        allVisibleSelected,
        someVisibleSelected,
        selectingAllPages,
        massModalOpen,
        setMassModalOpen,
        massOperationType,
        setMassOperationType,
        refundMode,
        setRefundMode,
        refundValue,
        setRefundValue,
        isExecutingMassAction,
        massExecutionProgress,
        massResults,
        executionPercent,
        hasCompletedExecution,
        isInvalidCancelRefundAmount,
        maxRefundableSummary,
        massRefundPreview,
        setBookingIncludedInMassAction,
        toggleRowSelection,
        selectAllRowsAcrossPages,
        clearSelection,
        executeMassOperation,
        resetMassState,
    } = useBookingsMassAction({
        rows,
        total,
        filters,
        canUseMassAction,
        isRowMassOperable,
        notifyError,
        notifySuccess,
        t,
        refreshBookings,
    });

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams((prev) => ({ ...prev, page: page - 1 }));
        }
    }, [rows, page, setParams]);

    const cancelReservation = useCallback(
        async (id, amount, { overrideCancelPolicy = false } = {}) => {
            setOperatingId(id);
            try {
                const reservationHash = rows.find((row) => row.id === id)?.reservationHash;
                await cancelBooking(id, {
                    amount,
                    reservationHash,
                    overrideCancelPolicy,
                });
                notifySuccess(t('bookings.notifications.cancelReservation'));
                navigate(`/bookings/edit/${id}`);
            } catch (_error) {
                notifyError(t('bookings.notifications.cancelReservationError'));
            } finally {
                setOperatingId(null);
            }
        },
        [notifyError, notifySuccess, t, navigate, rows],
    );

    const refundReservation = useCallback(
        async (id, amount) => {
            setOperatingId(id);
            try {
                await simpleRefundBooking(id, { amount });
                notifySuccess(t('common.refundReservation'));
                navigate(`/bookings/edit/${id}`);
            } catch (_error) {
                notifyError(t('errors.refundReservationError'));
            } finally {
                setOperatingId(null);
            }
        },
        [notifyError, notifySuccess, t, navigate],
    );

    const showMassSelectionPanel = canUseMassAction && selectedCount > 0;

    return (
        <Grid2 size={{ xs: 12 }}>
            {canUseMassAction && (
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        py: 0.5,
                        '& .MuiAlert-message': { width: '100%', textAlign: 'left' },
                    }}
                >
                    {t('bookings.massAction.selectAllHint')}
                </Alert>
            )}

            {showMassSelectionPanel && (
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                        p: 2,
                        mb: 2,
                    }}
                >
                    <Stack spacing={1.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'left' }}>
                            {t('bookings.massAction.selectedCount', { count: selectedCount })}
                        </Typography>

                        {selectingAllPages && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <CircularProgress size={14} />
                                {t('bookings.massAction.selectingAllPages')}
                            </Typography>
                        )}

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                            <Button
                                variant="outlined"
                                onClick={clearSelection}
                                disabled={selectingAllPages || isExecutingMassAction}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {t('bookings.massAction.clearSelection')}
                            </Button>
                            {showMassSelectionPanel && (
                                <Button
                                    variant="contained"
                                    onClick={() => setMassModalOpen(true)}
                                    disabled={selectingAllPages || isExecutingMassAction}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {t('bookings.massAction.button')}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            )}

            <TableContainer>
                <Table sx={{ minWidth: 1000 }}>
                    <TableHead>
                        <TableRow>
                            {canUseMassAction && (
                                <TableCell padding="checkbox">
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: 40,
                                            height: 40,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Tooltip
                                            title={t(
                                                'bookings.massAction.selectAllMatchingFilters',
                                            )}
                                        >
                                            <span>
                                                <Checkbox
                                                    checked={
                                                        allVisibleSelected && selectedCount > 0
                                                    }
                                                    indeterminate={someVisibleSelected}
                                                    onChange={selectAllRowsAcrossPages}
                                                    disabled={
                                                        loading ||
                                                        total === 0 ||
                                                        isExecutingMassAction ||
                                                        selectingAllPages
                                                    }
                                                />
                                            </span>
                                        </Tooltip>
                                        {selectingAllPages && (
                                            <CircularProgress
                                                size={18}
                                                sx={{
                                                    position: 'absolute',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </TableCell>
                            )}
                            <TableCell>{t('bookings.columns.bookingId')}</TableCell>
                            <TableCell>{t('bookings.columns.confirmationCode')}</TableCell>
                            {BOOKINGS_TABLE_SORTABLE_COLUMNS.map((column) => (
                                <SortableTableCell
                                    key={column.sortKey}
                                    arrow={sort === column.sortKey ? sortDir : ''}
                                    onClick={() => setSorting(column.sortKey)}
                                >
                                    {t(column.labelKey)}
                                </SortableTableCell>
                            ))}
                            <TableCell>{t('bookings.columns.ticketTypes')}</TableCell>
                            <TableCell>{t('bookings.columns.pax')}</TableCell>
                            <TableCell>{t('bookings.columns.source')}</TableCell>
                            <TableCell>{t('bookings.columns.paymentMethod')}</TableCell>
                            <TableCell>{t('bookings.columns.createdAt')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={canUseMassAction ? 11 : 10} />
                        ) : (
                            rows?.map((row) => (
                                <TableRow key={row.id}>
                                    {canUseMassAction && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedIds.has(row.id)}
                                                onChange={(event) =>
                                                    toggleRowSelection(row, event.target.checked)
                                                }
                                                disabled={
                                                    !isRowMassOperable(row) || isExecutingMassAction
                                                }
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>{row.confirmationCode || '-'}</TableCell>
                                    <TableCell>
                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                            <Chip
                                                style={{
                                                    background:
                                                        BOOKING_STATUS_COLORS[row.status] || '#ccc',
                                                    color: '#fff',
                                                }}
                                                label={t(
                                                    `widgetReservationInfo.statusValues.${row.status}`,
                                                )}
                                                size="small"
                                            />
                                            {row.isRebooked && (
                                                <AutorenewIcon
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -5,
                                                        right: -5,
                                                        fontSize: 15,
                                                        color: '#d9534f',
                                                        backgroundColor: '#fff',
                                                        borderRadius: '50%',
                                                        border: '1.5px solid #d9534f',
                                                        padding: '1px',
                                                    }}
                                                />
                                            )}
                                            {row.rollbackTimeAt && (
                                                <RollbackIndicator
                                                    rollbackTimeAt={row.rollbackTimeAt}
                                                    t={t}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {formatDateTimeInVenueTz(row.timeslot.startTime)}
                                    </TableCell>
                                    <TableCell>
                                        <GuestCell row={row} t={t} />
                                    </TableCell>
                                    <TableCell>
                                        <TicketTypesCell ticketTypes={row.ticketTypes} t={t} />
                                    </TableCell>
                                    <TableCell>{row.pax || 0}</TableCell>
                                    <TableCell>
                                        {row.source === 'online'
                                            ? row.agentId &&
                                              (row.commissionGroupNames?.length || row.agentName)
                                                ? `${
                                                      row.commissionGroupNames?.length
                                                          ? row.commissionGroupNames.join(', ')
                                                          : row.agentName
                                                  } / ${t('bookings.sourceOnline')}`
                                                : t('bookings.sourceOnline')
                                            : row.agentId
                                              ? (row.commissionGroupNames?.length
                                                    ? row.commissionGroupNames.join(', ')
                                                    : null) ||
                                                row.agentName ||
                                                t('bookings.sourceCallCenter')
                                              : row.integrationSource
                                                ? INTEGRATION_DISPLAY_NAMES[
                                                      row.integrationSource
                                                  ] || row.integrationSource
                                                : t('bookings.sourceOnline')}
                                    </TableCell>
                                    <TableCell>
                                        {row.paymentMethods?.length
                                            ? row.paymentMethods.join(' / ')
                                            : '-'}
                                    </TableCell>
                                    <TableCell>{formatDateTimeInVenueTz(row.createdAt)}</TableCell>
                                    <TableCell align="right">
                                        <BookingRowActions
                                            row={row}
                                            t={t}
                                            showModal={showModal}
                                            operatingId={operatingId}
                                            onRefundReservation={refundReservation}
                                            onCancelReservation={cancelReservation}
                                            canRefundBookings={canRefundBookings}
                                            canAlwaysRefund={canAlwaysRefund}
                                            canCancelBookings={canCancelBookings}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows?.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />

            <BookingsMassActionDialog
                open={massModalOpen}
                selectedCount={massSelectedCount}
                massOperationType={massOperationType}
                setMassOperationType={setMassOperationType}
                refundMode={refundMode}
                setRefundMode={setRefundMode}
                refundValue={refundValue}
                setRefundValue={setRefundValue}
                isExecutingMassAction={isExecutingMassAction}
                hasCompletedExecution={hasCompletedExecution}
                massResults={massResults}
                massExecutionProgress={massExecutionProgress}
                executionPercent={executionPercent}
                isInvalidCancelRefundAmount={isInvalidCancelRefundAmount}
                maxRefundableSummary={maxRefundableSummary}
                massRefundPreview={massRefundPreview}
                setBookingIncludedInMassAction={setBookingIncludedInMassAction}
                onClose={() => {
                    if (isExecutingMassAction) {
                        return;
                    }
                    if (hasCompletedExecution) {
                        clearSelection();
                        resetMassState();
                    } else {
                        setMassModalOpen(false);
                    }
                }}
                onExecute={executeMassOperation}
            />
        </Grid2>
    );
};

export default BookingsTable;
